import { BaseApiUrl } from '../../../configVariables';
import { IBrokerConnectionActiveConversationsData } from '../../../model/brokerConnections/IBrokerConnectionActiveConversationsData';
import { BrokerConnectionDetails } from '../../../model/brokerConnections/BrokerConnectionDetails';
import { ConversationsSummaryResponse } from '../../../model/summaries/ConversationsSummaryResponse';
import { ConversationNotesResponse } from '../../../model/brokerConnections/ConversationNotesResponse';
import { ConversationNotesRequest } from '../../../model/brokerConnections/ConversationNotesRequest';
import apiClient, { ApiResponse } from '../../../utils/apiClient';

export const activeConversationLengths = {
    note: { max: 4000 },
} as const;

export interface ConversationTeam {
    teamName: string;
    teamId: string;
    channelName: string;
    channelId: string;
}

export interface TransferConversationRequest {
    targetTeam: ConversationTeam;
    note: string;
}

const activeConversationsUrl = `${BaseApiUrl}/activeConversations`;

export const fetchUnresolvedActiveConversations = async (
    teamId?: string
): Promise<IBrokerConnectionActiveConversationsData[]> => {
    const result = await apiClient.get<
        IBrokerConnectionActiveConversationsData[]
    >(`${activeConversationsUrl}/unresolvedConversations`, {
        query: { teamId },
    });
    return result.data;
};

export const fetchActiveConversation = async (
    connectionId: string
): Promise<IBrokerConnectionActiveConversationsData | null> => {
    let notFound = false;

    const result =
        await apiClient.get<IBrokerConnectionActiveConversationsData>(
            `${activeConversationsUrl}/${connectionId}`,
            {
                errorResponseHandler: (resp, defaultHandler) => {
                    if (resp.status === 404) {
                        notFound = true;
                        return Promise.resolve(true);
                    }

                    return defaultHandler(resp);
                },
            }
        );

    return notFound ? null : result.data;
};

export const resolveActiveConversationsMessage = async (
    id: string,
    groundTeamRating?: number,
    groundTeamRatingComment?: string | null
): Promise<ApiResponse> => {
    return await apiClient.post(`${activeConversationsUrl}/${id}/resolve`, {
        groundTeamRating,
        groundTeamRatingComment,
    });
};

export const getBrokerConnectionDetails = async (
    brokerConnectionId: string
): Promise<BrokerConnectionDetails> => {
    const result = await apiClient.get<BrokerConnectionDetails>(
        `${activeConversationsUrl}/${brokerConnectionId}`
    );
    return result.data;
};

export const summariseConversations = async (
    teamId: string,
    startTime: Date,
    endTime: Date
): Promise<ConversationsSummaryResponse> => {
    const result = await apiClient.post<
        ConversationsSummaryResponse,
        { teamId: string; startTime: string; endTime: string }
    >(`${BaseApiUrl}/summaries/conversations`, {
        teamId,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
    });
    return result.data;
};

export const claimConversationOwnership = async (
    brokerConnectionId: string
): Promise<void> => {
    await apiClient.post(
        `${activeConversationsUrl}/${brokerConnectionId}/claim`,
        {
            brokerConnectionId,
        }
    );
};

export const transferConversation = async (
    brokerConnectionId: string,
    request: TransferConversationRequest
) => {
    await apiClient.post(
        `${activeConversationsUrl}/${brokerConnectionId}/transfer`,
        request
    );
};

export const fetchConversationNotes = async (
    conversationId: string
): Promise<ConversationNotesResponse[]> => {
    const result = await apiClient.get<ConversationNotesResponse[]>(
        `${activeConversationsUrl}/${conversationId}/conversationNotes`
    );
    return result.data;
};

export const addConversationNote = async (
    conversationId: string,
    noteRequest: ConversationNotesRequest
): Promise<void> => {
    await apiClient.post(
        `${activeConversationsUrl}/${conversationId}/conversationNotes`,
        noteRequest
    );
};

export const removeConversationNote = async (
    conversationId: string,
    noteId: string
): Promise<void> => {
    await apiClient.post(
        `${activeConversationsUrl}/${conversationId}/conversationNotes/${noteId}`
    );
};
